import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { authApi, clinicsApi } from "@/lib/api";
import type { ClinicOnboardingResponse, RegisterPayload } from "@/types/api";
import type { AppUser, Clinic } from "@/types/domain";

type AuthenticatedUser = AppUser | null;

interface AuthContextValue {
  user: AuthenticatedUser;
  clinics: Clinic[];
  selectedClinic: Clinic | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AppUser>;
  register: (userData: RegisterPayload) => Promise<ClinicOnboardingResponse>;
  logout: () => Promise<void>;
  selectClinic: (clinic: Clinic | null) => void;
  checkAuth: () => Promise<AppUser | null>;
  refreshClinics: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isAppUser(value: unknown): value is AppUser {
  if (!value || typeof value !== "object") {
    return false;
  }

  return typeof (value as AppUser).id === "string" && typeof (value as AppUser).email === "string";
}

function resolveUserClinic(user: AppUser, availableClinics: Clinic[]): Clinic | null {
  if (availableClinics.length === 0) {
    return null;
  }

  const clinicId = user.clinic_id ?? user.clinicId;

  if (!clinicId) {
    return null;
  }

  return availableClinics.find((clinic) => clinic.id === clinicId) ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data: currentUser } = await authApi.me();

      if (!isAppUser(currentUser)) {
        setUser(null);
        setClinics([]);
        setSelectedClinic(null);
        return null;
      }

      const { data: availableClinics } = await clinicsApi.list();

      setUser(currentUser);
      setClinics(availableClinics);
      setSelectedClinic(resolveUserClinic(currentUser, availableClinics));
      return currentUser;
    } catch {
      setUser(null);
      setClinics([]);
      setSelectedClinic(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email: string, password: string) => {
    await authApi.login({ email, password });
    const verifiedUser = await checkAuth();

    if (!verifiedUser) {
      throw new Error("Nao foi possivel validar a sessao autenticada.");
    }

    return verifiedUser;
  }, [checkAuth]);

  const refreshClinics = useCallback(async () => {
    if (!user) return;
    const { data: availableClinics } = await clinicsApi.list();
    setClinics(availableClinics);
    setSelectedClinic(resolveUserClinic(user, availableClinics));
  }, [user]);

  const register = useCallback(async (userData: RegisterPayload) => {
    const { data } = await authApi.onboardClinic(userData);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setClinics([]);
    setSelectedClinic(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      clinics,
      selectedClinic,
      loading,
      login,
      register,
      logout,
      selectClinic: setSelectedClinic,
      checkAuth,
      refreshClinics,
    }),
    [checkAuth, clinics, loading, login, logout, refreshClinics, register, selectedClinic, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
